import React, { useEffect, useMemo, useState } from "react";
import {
  FiClock,
  FiChevronLeft,
  FiChevronRight,
  FiFlag,
  FiGrid,
} from "react-icons/fi";
import { FaFlag } from "react-icons/fa";
import MultipleChoiceQuestion from "./MultipleChoiceQuestion";
import FillInTheBlankQuestion from "./FillInTheBlankQuestion";
import TrueFalseQuestion from "./TrueFalseQuestion";
import ShortAnswerQuestion from "./ShortAnswerQuestion";

// ============================================
// VALIDATION AND SECURITY FUNCTIONS
// ============================================
//
// SECURITY FEATURES IMPLEMENTED:
// 1. XSS Prevention: Sanitizes all input to prevent cross-site scripting attacks
// 2. SQL Injection Prevention: Removes SQL injection patterns from input
// 3. JavaScript Injection Prevention: Blocks javascript: and data: protocols
// 4. HTML Tag Removal: Strips HTML tags from input
// 5. Copy/Paste Protection: Prevents pasting malicious content
// 6. Input Length Limits: Enforces reasonable length limits
// 7. Character Validation: Validates character sets for each field type
// 8. Anti-Spam Protection: Blocks common spam patterns and test data
// 9. Format Validation: Ensures proper format for email and phone
// 10. Real-time Validation: Provides immediate feedback on input validity
// 11. Visual Feedback: Shows success/error states with colors and icons
// 12. Submission Protection: Prevents submission until all validation passes

/**
 * Sanitize input to prevent XSS attacks and other security vulnerabilities
 */
const sanitizeInput = (input) => {
  if (!input || typeof input !== "string") return "";

  return (
    input
      .replace(/[<>\"'&]/g, (match) => {
        const map = {
          "<": "<",
          ">": ">",
          '"': '"',
          "'": "'",
          "&": "&",
        };
        return map[match];
      })
      // Remove SQL injection patterns
      .replace(
        /(\\|%|;|--|\/\*|\*\/|xp_|sp_|exec|select|insert|update|delete|drop|create|alter|union|script)/gi,
        ""
      )
      // Remove JavaScript injection patterns
      .replace(
        /(javascript:|data:|vbscript:|onload|onerror|onclick|onmouseover)/gi,
        ""
      )
      // Remove HTML tags
      .replace(/<[^>]*>/g, "")
      .trim()
  );
};

/**
 * Validate name input
 */
const validateName = (name) => {
  const errors = [];

  // XSS prevention
  const sanitized = sanitizeInput(name);

  // Length validation
  if (sanitized.length < 2) {
    errors.push("Name must be at least 2 characters long");
  }

  if (sanitized.length > 100) {
    errors.push("Name cannot exceed 100 characters");
  }

  // Character validation (letters, spaces, hyphens, apostrophes only)
  const nameRegex = /^[a-zA-Z\s\-']{2,100}$/;
  if (sanitized && !nameRegex.test(sanitized)) {
    errors.push(
      "Name can only contain letters, spaces, hyphens, and apostrophes"
    );
  }

  // No consecutive special characters (but allow spaces)
  if (
    sanitized.includes("--") ||
    sanitized.includes("''")
  ) {
    errors.push("Name cannot contain consecutive special characters");
  }

  // No leading/trailing special characters or spaces
  if (sanitized.match(/^[^a-zA-Z]/) || sanitized.match(/[^a-zA-Z]$/)) {
    errors.push("Name must start and end with a letter");
  }

  // Anti-spam validation
  const spamPatterns = [
    /^(test|demo|user|admin|guest|temp|sample|example)/i,
    /^(aaaa|bbbb|cccc|1111|2222|3333)/i,
    /^(abc|xyz|qwe|asd|zxc)/i,
  ];

  if (sanitized && spamPatterns.some((pattern) => pattern.test(sanitized))) {
    errors.push("Please enter a valid name");
  }

  return {
    isValid: errors.length === 0,
    errors,
    sanitized,
  };
};
/**
 * Validate email input
 */
const validateEmail = (email) => {
  const errors = [];

  // XSS prevention
  const sanitized = sanitizeInput(email);

  // Length validation
  if (sanitized.length > 254) {
    errors.push("Email cannot exceed 254 characters");
  }

  // Basic email format validation
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  if (sanitized && !emailRegex.test(sanitized)) {
    errors.push("Please enter a valid email address");
  }

  // Additional security checks
  if (sanitized.includes("script") || sanitized.includes("javascript")) {
    errors.push("Invalid email format");
  }

  // Domain validation (basic)
  if (sanitized.includes("@")) {
    const domain = sanitized.split("@")[1];
    if (domain && (domain.length < 3 || domain.includes(".."))) {
      errors.push("Invalid email domain");
    }
  }

  // Anti-spam validation
  const spamPatterns = [
    /^(test|demo|user|admin|guest|temp|sample|example)/i,
    /^(aaaa|bbbb|cccc|1111|2222|3333)/i,
    /^(abc|xyz|qwe|asd|zxc)/i,
  ];

  if (
    sanitized &&
    spamPatterns.some((pattern) => pattern.test(sanitized.split("@")[0]))
  ) {
    errors.push("Please enter a valid email address");
  }

  return {
    isValid: errors.length === 0,
    errors,
    sanitized,
  };
};

/**
 * Validate phone input
 */
const validatePhone = (phone) => {
  const errors = [];

  // XSS prevention
  const sanitized = sanitizeInput(phone);

  // Remove all non-digit characters for validation
  const digitsOnly = sanitized.replace(/\D/g, "");

  // Length validation (most international numbers are 10-15 digits)
  if (digitsOnly.length < 7) {
    errors.push("Phone number must be at least 7 digits");
  }

  if (digitsOnly.length > 15) {
    errors.push("Phone number cannot exceed 15 digits");
  }

  // Format validation (allow common formats)
  const phoneRegex = /^[\+]?[\d\s\-\(\)]{7,20}$/;
  if (sanitized && !phoneRegex.test(sanitized)) {
    errors.push("Please enter a valid phone number");
  }

  // No repeated digits (potential spam prevention)
  if (digitsOnly.length > 3) {
    const hasRepeating = /(\d)\1{5,}/.test(digitsOnly);
    if (hasRepeating) {
      errors.push("Phone number cannot have repeated digits");
    }
  }

  // Anti-spam validation
  const spamPatterns = [
    /^(123|456|789|000|111|222|333|444|555|666|777|888|999)/,
    /^(555|999)/,
    /^(012|234|345|456|567|678|789)/,
  ];

  if (digitsOnly && spamPatterns.some((pattern) => pattern.test(digitsOnly))) {
    errors.push("Please enter a valid phone number");
  }

  return {
    isValid: errors.length === 0,
    errors,
    sanitized,
  };
};

// ============================================
// PROFILE QUESTION IDS - EXACT ORDER
// These IDs are from your actual API response
// ============================================

const PROFILE_QUESTIONS_ORDER = [
  // Contact Information (placed at the beginning of profile questions)
  "cmjdzca9i00yyczak45q0j25t", // Please type your email address:
  "cmjdzckzv00z2czakmp3tb4vt", // Please type your phone number:

  // ===== SECTION 1: Professional Background =====
  "cmjd6m2yh00iwcz0h3q5x6yq4", // What is your current job position/title at AKAH?
  "cmjd6m2ya00incz0her23pykb",
  "cmjfafk5i00wcczv49j3dmb3p", // How many years of professional work experience do you have?
  "cmjd6m2xy00iecz0h6t5bhbjk", // What is your educational level?
  "cmjd6m2yo00j7cz0hchsrutdy", // Which department/unit do you work in?
  "cmjd6nc0100l6cz0hgl50io9h", // Other department (please specify):

  // ===== SECTION 2: Technical Setup & Preferences =====
  "cmjd6m2z800k2cz0h268hs0pp", // Do you have administrative rights to install add-ins...
  "cmjdwqh1t00hsczaksuk9k3rp", // Same question (duplicate)
  "cmjd6m2yw00jjcz0hgf9bxo9a", // Which version of Excel do you primarily use?
  "cmjd6m2z300jtcz0hbxqrg4o0", // What is your preferred language for training instruction?
  "cmjdwq8mr00hjczakh5mnbfgp", // Same question (duplicate)

  // ===== SECTION 3: Work Context & Experience =====
  "cmjdwi15v00f2czakb47cxqoy", // How frequently do you use Excel in your current role?
  "cmjdwgmvj00eeczakogosnx95", // What type of projects do you primarily work on?
  "cmjdwirng00fcczak7rojmeaf", // For what primary purposes do you use Excel at AKAH?
  "cmjdwky5y00fpczakxbztqnd7", // What are the main types of data you work with in Excel?

  // ===== SECTION 4: Training Needs & Preferences =====
  "cmjdwpxe800h5czak1zr0rqkv", // What specific skills do you most hope to gain...
  "cmjdwoyvt00gwczakrqmencnm", // Rate your current confidence level in these areas...
  "cmjdwnq6p00giczak7qcc5bvv", // What are your biggest challenges when working with Excel data?
  "cmjdwms0n00g2czakqfcbulfz", // Which of these Excel functions are you already familiar with?

  // ===== SECTION 5: Accessibility & Learning Preferences =====
  "cmjdw8crw00dtczak4n8g6ly7", // Do you have any accessibility requirements...
  "cmjd6gad100i8cz0hizztsa4w", // Same topic (TRUE_FALSE version)

  // ===== SECTION 6: Post-Training Application =====
  "cmjdwrex100i0czak68o31kug", // After the workshop, how do you plan to apply these Excel skills...

  // ===== Existing Questions (keeping for backward compatibility) =====
  "cmjd6nbzp00kycz0hof5u992x", // Field of education (please specify):
  "cmjd6nbzv00l2cz0ht8ns1o3v", // Other job position (please specify):
  "cmjd6nc0500lacz0homgiq8pk", // Other types of projects you work on (please specify):
  "cmjd6nc0900lecz0hzj985689", // Other types of data you work with (please specify):
  "cmjd6nc0d00licz0hs4dj23dz", // Other Excel challenges you face (please specify):
  "cmjd6nc0h00lmcz0h9i5v40qh", // Other specific skills you hope to gain (please specify):
  "cmjd6nc0l00lqcz0hjtcv7jfp", // After the workshop, how do you plan to apply these Excel skills?
  "cmjd6n3nm00kacz0hlm857qz1", // Do you have any accessibility requirements?
  "cmjd6n3nx00kgcz0hsj0zevsq", // Do you need larger text/visual aids?
  "cmjd6n3o400kmcz0hehhxrks2", // Do you prefer hands-on practice over lectures?
  "cmjd6n3oa00kscz0h1dor8pyz", // Do you need additional explanation time?
  "cmjd6nc0p00lucz0hg74erjh0", // Other accessibility requirements (please specify):
];

// Create a Set for O(1) lookup
const PROFILE_QUESTION_IDS = new Set(PROFILE_QUESTIONS_ORDER);

// Section boundaries for headers
const SECTION_CONFIG = {
  cmjdzca9i00yyczak45q0j25t: {
    section: 1,
    title: "Contact Information",
    subtitle: "Your contact details",
  },
  cmjd6m2yh00iwcz0h3q5x6yq4: {
    section: 2,
    title: "Section 1: Professional Background",
    subtitle: "Your background information",
  },
  cmjd6m2z800k2cz0h268hs0pp: {
    section: 3,
    title: "Section 2: Technical Setup & Preferences",
    subtitle: "Your technical environment",
  },
  cmjdwi15v00f2czakb47cxqoy: {
    section: 4,
    title: "Section 3: Work Context & Experience",
    subtitle: "Your Excel usage context",
  },
  cmjdwpxe800h5czak1zr0rqkv: {
    section: 5,
    title: "Section 4: Training Needs & Preferences",
    subtitle: "Your learning objectives",
  },
  cmjdw8crw00dtczak4n8g6ly7: {
    section: 6,
    title: "Section 5: Accessibility & Learning Preferences",
    subtitle: "Your accessibility needs",
  },
  cmjdwrex100i0czak68o31kug: {
    section: 7,
    title: "Section 6: Post-Training Application",
    subtitle: "Your planned application of skills",
  },
  cmjd6m2yw00jjcz0hgf9bxo9a: {
    section: 8,
    title: "Additional Profile Questions",
    subtitle: "Supplementary information",
  },
  cmjd6m2z300jtcz0hbxqrg4o0: {
    section: 9,
    title: "Language & Communication",
    subtitle: "Your communication preferences",
  },
};

// First technical question marker
const TECHNICAL_SECTION = {
  section: 10,
  title: "Excel Skills Assessment",
  subtitle: "Test your Excel knowledge",
};

/**
 * Sort questions: Profile questions first (in order), then technical questions
 * Email and phone questions are placed immediately after the name question
 */
const sortQuestionsWithProfileFirst = (questions) => {
  if (!questions || !Array.isArray(questions)) return [];

  console.log("🔄 Starting question sort...");
  console.log(`📊 Total questions: ${questions.length}`);

  // Separate profile and technical questions
  const profileQuestions = [];
  const technicalQuestions = [];

  questions.forEach((question) => {
    // Skip the "Other department (please specify)" question
    if (question.id === "cmjd6nc0100l6cz0hgl50io9h") {
      return;
    }

    if (PROFILE_QUESTION_IDS.has(question.id)) {
      profileQuestions.push(question);
    } else {
      technicalQuestions.push(question);
    }
  });

  console.log(`👤 Profile questions found: ${profileQuestions.length}`);
  console.log(`📝 Technical questions found: ${technicalQuestions.length}`);

  // Sort profile questions according to predefined order
  profileQuestions.sort((a, b) => {
    const indexA = PROFILE_QUESTIONS_ORDER.indexOf(a.id);
    const indexB = PROFILE_QUESTIONS_ORDER.indexOf(b.id);
    return indexA - indexB;
  });

  // Extract email and phone questions if they exist in profile questions
  let emailQuestion = null;
  let phoneQuestion = null;
  const remainingProfileQuestions = [];

  profileQuestions.forEach((question) => {
    if (question.id === "cmjdzca9i00yyczak45q0j25t") {
      emailQuestion = question;
    } else if (question.id === "cmjdzckzv00z2czakmp3tb4vt") {
      phoneQuestion = question;
    } else {
      remainingProfileQuestions.push(question);
    }
  });

  // Log first few profile questions for debugging
  console.log("✅ Profile questions (first 5):");
  profileQuestions.slice(0, 5).forEach((q, i) => {
    console.log(`   ${i + 1}. ${q.text?.substring(0, 50)}...`);
  });

  // Prepend synthetic name question
  const nameQuestion = {
    id: "participant_name",
    text: "Please enter your name",
    type: "SHORT_ANSWER",
    difficulty: "EASY",
    marks: 0,
    options: [],
  };

  // Combine: name question, email question, phone question, remaining profile questions, then technical questions
  const sortedQuestions = [
    nameQuestion,
    ...(emailQuestion ? [emailQuestion] : []),
    ...(phoneQuestion ? [phoneQuestion] : []),
    ...remainingProfileQuestions,
    ...technicalQuestions,
  ];

  console.log("✅ Sorting complete!");
  console.log(
    `📋 First few questions:`,
    sortedQuestions.slice(0, 3).map((q) => q.text)
  );

  return sortedQuestions;
};

/**
 * Check if question needs a section header
 */
const getSectionHeader = (question, isFirstTechnical = false) => {
  if (SECTION_CONFIG[question.id]) {
    return SECTION_CONFIG[question.id];
  }
  if (isFirstTechnical) {
    return TECHNICAL_SECTION;
  }
  return null;
};

// ============================================
// MAIN COMPONENT
// ============================================

const InternshipInterface = ({
  showFullScreenQuestion,
  setShowFullScreenQuestion,
  currentQuestion,
  setCurrentQuestion,
  questions: rawQuestions,
  currentQuestionData,
  timeLeft,
  examProgress,
  flaggedQuestions,
  toggleQuestionFlag,
  answers,
  handleAnswerSelect,
  handleFillInTheBlankAnswer,
  handleSubmit,
  showInstructions,
  toggleInstructions,
  showCalculator,
  setShowCalculator,
  showQuestionNavigator,
  setShowQuestionNavigator,
  exam,
  isInternshipExam,
  // Add props for public access
  isPublic = false,
  publicQuestions = [],
  publicExam = {},
  // Add participant name props
  participantName: externalParticipantName,
  setParticipantName: externalSetParticipantName,
}) => {
  // Participant name state for the first synthetic question
  const [internalParticipantName, setInternalParticipantName] = useState("");

  // Validation state for contact info inputs
  const [nameErrors, setNameErrors] = useState([]);
  const [emailErrors, setEmailErrors] = useState([]);
  const [phoneErrors, setPhoneErrors] = useState([]);
  const [nameTouched, setNameTouched] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);

  // Use external props if provided, otherwise use internal state
  const participantName =
    externalParticipantName !== undefined
      ? externalParticipantName
      : internalParticipantName;
  const setParticipantName =
    externalSetParticipantName !== undefined
      ? externalSetParticipantName
      : setInternalParticipantName;

  // Use public data if in public mode, otherwise use regular data
  const effectiveQuestions = isPublic ? publicQuestions : rawQuestions;
  const effectiveExam = isPublic ? publicExam : exam;

  // ============================================
  // SORT QUESTIONS - Profile First
  // ============================================
  const questions = useMemo(() => {
    return sortQuestionsWithProfileFirst(effectiveQuestions);
  }, [effectiveQuestions]);

  // Track first technical question index
  const firstTechnicalIndex = useMemo(() => {
    // Find the first question that is not a profile question (excluding the synthetic name question)
    return questions.findIndex(
      (q, index) =>
        index > 0 &&
        !PROFILE_QUESTION_IDS.has(q.id) &&
        q.id !== "participant_name"
    );
  }, [questions]);

  // Local fallbacks
  const [localFlaggedQuestions, setLocalFlaggedQuestions] = React.useState(
    new Set()
  );
  const [localShowNavigator, setLocalShowNavigator] = React.useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = React.useState(0);
  const questionsPerPage = 10;

  // Calculate pagination
  const startIndex = currentPage * questionsPerPage;
  const endIndex = Math.min(startIndex + questionsPerPage, questions.length);
  const currentQuestions = questions.slice(startIndex, endIndex);

  const effectiveFlaggedQuestions =
    flaggedQuestions instanceof Set ? flaggedQuestions : localFlaggedQuestions;
  const effectiveToggleFlag =
    typeof toggleQuestionFlag === "function"
      ? toggleQuestionFlag
      : (questionIndex) => {
          setLocalFlaggedQuestions((prev) => {
            const next = new Set(prev);
            if (next.has(questionIndex)) next.delete(questionIndex);
            else next.add(questionIndex);
            return next;
          });
        };

  const effectiveShowQuestionNavigator =
    typeof showQuestionNavigator === "boolean"
      ? showQuestionNavigator
      : localShowNavigator;
  const effectiveSetShowQuestionNavigator =
    typeof setShowQuestionNavigator === "function"
      ? setShowQuestionNavigator
      : setLocalShowNavigator;

  // Copy protection and security measures
  useEffect(() => {
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };
    const handleSelectStart = (e) => {
      e.preventDefault();
      return false;
    };
    const handleDragStart = (e) => {
      e.preventDefault();
      return false;
    };
    const handleKeyDown = (e) => {
      if (
        (e.ctrlKey &&
          ["c", "C", "x", "X", "v", "V", "a", "A", "s", "S", "u", "U"].includes(
            e.key
          )) ||
        e.key === "F12" ||
        (e.ctrlKey && e.shiftKey && ["I", "i"].includes(e.key))
      ) {
        e.preventDefault();
        return false;
      }
    };

    // Additional security: prevent paste of malicious content in contact fields
    const handlePaste = (e) => {
      const pastedData = e.clipboardData.getData("text");
      if (
        pastedData.includes("<script") ||
        pastedData.includes("javascript:")
      ) {
        e.preventDefault();
        return false;
      }
    };

    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("selectstart", handleSelectStart);
    document.addEventListener("dragstart", handleDragStart);
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("paste", handlePaste);

    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("selectstart", handleSelectStart);
      document.removeEventListener("dragstart", handleDragStart);
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("paste", handlePaste);
    };
  }, []);

  useEffect(() => {
    const scrollContainer = document.querySelector(".question-area");
    const headerContent = document.querySelector(".header-content");
    const statsRow = document.querySelector(".stats-row");

    if (!scrollContainer || !headerContent || !statsRow) return;

    const onScroll = () => {
      if (scrollContainer.scrollTop > 50) {
        headerContent.style.display = "none";

        statsRow.style.position = "fixed";
        statsRow.style.top = "0";
        statsRow.style.left = "0";
        statsRow.style.right = "0";
        statsRow.style.zIndex = "9999";
        statsRow.style.background = "rgba(255,255,255,0.95)";
        statsRow.style.boxShadow = "0 2px 10px rgba(0,0,0,0.1)";
        statsRow.style.padding = "8px 20px";
      } else {
        headerContent.style.display = "block";

        statsRow.style.position = "static";
        statsRow.style.boxShadow = "none";
        statsRow.style.background = "transparent";
        statsRow.style.padding = "0";
      }
    };

    scrollContainer.addEventListener("scroll", onScroll, { passive: true });
    return () => scrollContainer.removeEventListener("scroll", onScroll);
  }, []);

  // Validation summary for contact info
  const contactInfoValid = useMemo(() => {
    const nameValidation = validateName(participantName);
    const emailValidation = validateEmail(
      answers["cmjdzca9i00yyczak45q0j25t"] || ""
    );
    const phoneValidation = validatePhone(
      answers["cmjdzckzv00z2czakmp3tb4vt"] || ""
    );

    const allFieldsFilled =
      (nameValidation.sanitized || "").trim().length > 0 &&
      (emailValidation.sanitized || "").trim().length > 0 &&
      (phoneValidation.sanitized || "").trim().length > 0;

    return {
      name: nameValidation,
      email: emailValidation,
      phone: phoneValidation,
      allValid:
        nameValidation.isValid &&
        emailValidation.isValid &&
        phoneValidation.isValid &&
        allFieldsFilled,
      allFilled: allFieldsFilled,
    };
  }, [participantName, answers]);

  // Enhanced submit handler with validation
  const handleSubmitWithValidation = (name) => {
    // Validate all contact info before submission
    const nameValidation = validateName(name || participantName);
    const emailValidation = validateEmail(
      answers["cmjdzca9i00yyczak45q0j25t"] || ""
    );
    const phoneValidation = validatePhone(
      answers["cmjdzckzv00z2czakmp3tb4vt"] || ""
    );

    // Mark all fields as touched
    setNameTouched(true);
    setEmailTouched(true);
    setPhoneTouched(true);

    // Update error states
    setNameErrors(nameValidation.errors);
    setEmailErrors(emailValidation.errors);
    setPhoneErrors(phoneValidation.errors);

    // Check if all contact info is valid
    const allValid =
      nameValidation.isValid &&
      emailValidation.isValid &&
      phoneValidation.isValid;

    // Additional check: ensure all fields have been filled
    const allFieldsFilled =
      (nameValidation.sanitized || "").trim().length > 0 &&
      (emailValidation.sanitized || "").trim().length > 0 &&
      (phoneValidation.sanitized || "").trim().length > 0;

    if (!allValid || !allFieldsFilled) {
      // Scroll to first invalid field
      const firstInvalidField =
        !nameValidation.isValid ||
        (nameValidation.sanitized || "").trim().length === 0
          ? "name"
          : !emailValidation.isValid ||
            (emailValidation.sanitized || "").trim().length === 0
          ? "email"
          : "phone";

      // Focus on the first invalid field
      setTimeout(() => {
        const element = document.querySelector(
          `input[placeholder="${
            firstInvalidField === "name"
              ? "Your full name"
              : firstInvalidField === "email"
              ? "user@domain.com"
              : "+1234567890"
          }"]`
        );
        if (element) {
          element.focus();
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);

      return; // Prevent submission
    }

    // All validation passed, proceed with submission
    console.log(
      "✅ All contact info validation passed, proceeding with submission"
    );
    if (handleSubmit) {
      handleSubmit(nameValidation.sanitized);
    }
  };

  // Early returns
  if (!showFullScreenQuestion) return null;
  if (!questions || questions.length === 0) return null;

  // Helpers
  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const navigateToQuestion = (questionIndex) => {
    setCurrentQuestion(questionIndex);
    setCurrentPage(Math.floor(questionIndex / questionsPerPage));
    effectiveSetShowQuestionNavigator(false);
    setShowFullScreenQuestion(true);
    // Scroll to top of question area
    setTimeout(() => {
      const questionArea = document.querySelector(".question-area");
      if (questionArea) {
        questionArea.scrollTop = 0;
      }
    }, 0);
  };

  const isMockExam =
    typeof exam?.title === "string" &&
    exam.title.toLowerCase().includes("mock");

  // Answer handlers
  const handleMultipleChoiceAnswer = (questionId, answerData) => {
    if (handleAnswerSelect) handleAnswerSelect(questionId, answerData);
  };

  // Handler for "Other" text inputs in multiple choice questions
  const handleMultipleChoiceOtherText = (questionId, text) => {
    if (handleAnswerSelect)
      handleAnswerSelect(`${questionId}_other_text`, text);
  };

  // New handler for multiple choice questions with text inputs
  const handleMultipleChoiceWithTextInput = (questionId, answerData) => {
    if (handleAnswerSelect) handleAnswerSelect(questionId, answerData);
  };

  const handleFillInTheBlankAnswerWrapper = (questionId, blankAnswers) => {
    if (
      typeof blankAnswers === "object" &&
      !Array.isArray(blankAnswers) &&
      blankAnswers !== null
    ) {
      const answerArray = Object.keys(blankAnswers)
        .sort((a, b) => parseInt(a) - parseInt(b))
        .map((key) => blankAnswers[key] || "");
      if (handleFillInTheBlankAnswer)
        handleFillInTheBlankAnswer(questionId, answerArray);
    } else {
      if (handleFillInTheBlankAnswer)
        handleFillInTheBlankAnswer(questionId, blankAnswers);
    }
  };

  const handleTrueFalseAnswer = (questionId, optionId) => {
    if (handleAnswerSelect) handleAnswerSelect(questionId, optionId);
  };

  const handleShortAnswerChange = (questionId, value) => {
    if (handleAnswerSelect) handleAnswerSelect(questionId, value);
  };

  // Enhanced name change handler with validation
  const handleNameChange = (e) => {
    const newName = e.target.value;

    // Prevent common attack patterns
    if (newName.includes("<script") || newName.includes("javascript:")) {
      return; // Block malicious input
    }

    // For name input, we want to preserve spaces as the user types
    // We'll sanitize only when validating, not while typing
    setParticipantName(newName);

    // Validate but don't immediately show errors while typing
    const validation = validateName(newName);
    
    // Update validation errors (only show after user has interacted)
    if (nameTouched) {
      setNameErrors(validation.errors);
    }

    // Mark as touched on first change
    if (!nameTouched) {
      setNameTouched(true);
    }
  };
  // Enhanced email/phone change handler with validation
  const handleContactInfoChange = (questionId, value) => {
    // Prevent common attack patterns
    if (value.includes("<script") || value.includes("javascript:")) {
      return; // Block malicious input
    }

    let validation;
    let setError;
    let isTouched;
    let setTouched;

    if (questionId === "cmjdzca9i00yyczak45q0j25t") {
      // Email validation
      validation = validateEmail(value);
      setError = setEmailErrors;
      isTouched = emailTouched;
      setTouched = setEmailTouched;
    } else if (questionId === "cmjdzckzv00z2czakmp3tb4vt") {
      // Phone validation
      validation = validatePhone(value);
      setError = setPhoneErrors;
      isTouched = phoneTouched;
      setTouched = setPhoneTouched;
    }

    if (validation) {
      // Update errors (only show after user has interacted)
      if (isTouched) {
        setError(validation.errors);
      } else {
        setTouched(true);
      }

      // Update answer with sanitized value
      if (handleAnswerSelect)
        handleAnswerSelect(questionId, validation.sanitized);
    }
  };

  // ============================================
  // RENDER SECTION HEADER COMPONENT
  // ============================================
  const SectionHeader = ({ title, subtitle, icon, isContactInfo }) => (
    <div
      style={{
        background: isContactInfo
          ? "linear-gradient(135deg, #f5f7fa 0%, #e4e7f1 100%)" // Lighter gradient for contact info
          : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        color: isContactInfo ? "#333" : "#ffffff",
        padding: isContactInfo ? "15px 20px" : "20px 24px", // Less padding for contact info
        borderRadius: "12px",
        marginBottom: "24px",
        boxShadow: isContactInfo
          ? "0 2px 10px rgba(0, 0, 0, 0.05)" // Subtle shadow for contact info
          : "0 4px 20px rgba(102, 126, 234, 0.4)",
        display: "flex",
        flexDirection: "column",
        gap: "4px",
      }}
    >
      <span
        style={{
          fontSize: isContactInfo ? "18px" : "20px",
          fontWeight: isContactInfo ? "600" : "700",
        }}
      >
        {title}
      </span>
      {subtitle && (
        <span
          style={{
            fontSize: isContactInfo ? "13px" : "14px",
            opacity: isContactInfo ? 0.8 : 0.9,
          }}
        >
          {subtitle}
        </span>
      )}
    </div>
  );

  // ============================================
  // RENDER QUESTION COMPONENT
  // ============================================
  const renderQuestion = (question, globalIndex) => {
    const questionType = question?.type;
    const isProfileQuestion = PROFILE_QUESTION_IDS.has(question.id);

    // Check if this is one of our special contact info questions
    const isContactInfoQuestion =
      question.id === "cmjdzca9i00yyczak45q0j25t" ||
      question.id === "cmjdzckzv00z2czakmp3tb4vt";

    const questionWrapper = (children) => (
      <div
        key={question.id}
        className="no-copy"
        style={{
          background: "#ffffff",
          border: isProfileQuestion ? "2px solid #667eea" : "1px solid #e0e0e0",
          borderRadius: "12px",
          padding: isContactInfoQuestion ? "16px 20px" : "24px", // Less padding for contact info
          marginBottom: "20px",
          boxShadow: isProfileQuestion
            ? "0 4px 15px rgba(102, 126, 234, 0.15)"
            : "0 2px 10px rgba(0, 0, 0, 0.05)",
          width: "100%",
          position: "relative",
        }}
      >
        {/* Question Type Badge */}
        <div
          style={{
            position: "absolute",
            top: "12px",
            right: "12px",
            display: "flex",
            gap: "8px",
          }}
        >
          {isProfileQuestion && !isContactInfoQuestion && (
            <span
              style={{
                background: "#667eea",
                color: "white",
                padding: "4px 10px",
                borderRadius: "12px",
                fontSize: "11px",
                fontWeight: "600",
              }}
            >
              Profile
            </span>
          )}
          {!isContactInfoQuestion && (
            <span
              style={{
                background: "#f0f0f0",
                color: "#666",
                padding: "4px 10px",
                borderRadius: "12px",
                fontSize: "11px",
                fontWeight: "500",
              }}
            >
              Q{globalIndex + 1}
            </span>
          )}
        </div>
        {children}
      </div>
    );

    // MULTIPLE_CHOICE
    if (questionType === "MULTIPLE_CHOICE") {
      // Special handling for the star rating self-assessment question
      if (question.id === "cmjdwoyvt00gwczakrqmencnm") {
        // Get the current rating
        const currentRating =
          answers[question.id] !== undefined ? answers[question.id] : null;

        return questionWrapper(
          <div>
            <div
              style={{
                fontSize: "16px",
                lineHeight: "1.6",
                color: "#333",
                fontWeight: "500",
                marginBottom: "20px",
                paddingRight: "80px",
              }}
            >
              {question?.text}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {[1, 2, 3, 4, 5].map((starValue) => {
                const isActive = currentRating >= starValue;
                return (
                  <button
                    key={starValue}
                    type="button"
                    onClick={() =>
                      handleMultipleChoiceAnswer(question.id, starValue)
                    }
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: "4px",
                      fontSize: "24px",
                      color: isActive ? "#fbbf24" : "#d1d5db",
                      transition: "color 0.2s",
                    }}
                    aria-label={`${starValue} star${starValue > 1 ? "s" : ""}`}
                  >
                    ★
                  </button>
                );
              })}
              <span
                style={{
                  fontSize: "14px",
                  color: "#666",
                  marginLeft: "12px",
                  minWidth: "60px",
                }}
              >
                {currentRating !== null
                  ? `${currentRating} star${currentRating !== 1 ? "s" : ""}`
                  : "Select rating"}
              </span>
            </div>            <div
              style={{
                marginTop: "12px",
                fontSize: "13px",
                color: "#888",
                fontStyle: "italic",
              }}
            >
              1 star = Not confident, 5 stars = Very confident
            </div>
          </div>
        );
      }

      return questionWrapper(
        <MultipleChoiceQuestion
          question={question}
          answers={answers}
          onAnswerChange={handleMultipleChoiceAnswer}
          onOtherTextChange={handleMultipleChoiceOtherText}
          questionIndex={globalIndex}
        />
      );
    }

    // TRUE_FALSE
    if (questionType === "TRUE_FALSE") {
      return questionWrapper(
        <TrueFalseQuestion
          question={question}
          answers={answers}
          onAnswerChange={handleTrueFalseAnswer}
          questionIndex={globalIndex}
        />
      );
    }

    // FILL_IN_THE_BLANK
    if (questionType === "FILL_IN_THE_BLANK") {
      const convertedAnswers = { ...answers };
      if (answers[question.id] && Array.isArray(answers[question.id])) {
        const answerArray = answers[question.id];
        const answerObject = {};
        answerArray.forEach((value, idx) => {
          if (value !== undefined && value !== null && value !== "") {
            answerObject[idx] = value;
          }
        });
        convertedAnswers[question.id] = answerObject;
      }

      return questionWrapper(
        <FillInTheBlankQuestion
          question={question}
          answers={convertedAnswers}
          onAnswerChange={handleFillInTheBlankAnswerWrapper}
          questionIndex={globalIndex}
        />
      );
    }

    // SHORT_ANSWER
    if (questionType === "SHORT_ANSWER") {
      // Special styling for contact info questions
      if (isContactInfoQuestion) {
        const isEmail = question.id === "cmjdzca9i00yyczak45q0j25t";
        const isPhone = question.id === "cmjdzckzv00z2czakmp3tb4vt";
        const currentErrors = isEmail ? emailErrors : phoneErrors;
        const isTouched = isEmail ? emailTouched : phoneTouched;
        const inputType = isEmail ? "email" : "tel";
        const placeholder = isEmail ? "user@domain.com" : "+1234567890";
        const autoComplete = isEmail ? "email" : "tel";

        return questionWrapper(
          <div style={{ position: "relative" }}>
            <label
              style={{
                fontSize: "14px",
                color: "#555",
                fontWeight: "500",
                marginBottom: "8px",
                display: "block",
              }}
            >
              {question?.text}
            </label>
            <input
              type={inputType}
              value={answers[question.id] || ""}
              onChange={(e) =>
                handleContactInfoChange(question.id, e.target.value)
              }
              onBlur={() => {
                if (isEmail) setEmailTouched(true);
                if (isPhone) setPhoneTouched(true);
              }}
              placeholder={placeholder}
              style={{
                width: "100%",
                padding: "10px 12px",
                border:
                  currentErrors.length > 0 && isTouched
                    ? "2px solid #ef4444"
                    : isTouched && currentErrors.length === 0
                    ? "2px solid #22c55e"
                    : "1px solid #ddd",
                borderRadius: "6px",
                fontSize: "14px",
                fontFamily: "inherit",
                outline: "none",
                transition: "border-color 0.2s ease",
              }}
              onFocus={(e) => {
                if (!(currentErrors.length > 0 && isTouched)) {
                  e.target.style.borderColor = "#667eea";
                }
              }}
              maxLength={isEmail ? 254 : 20}
              autoComplete={autoComplete}
              aria-describedby={
                currentErrors.length > 0 && isTouched
                  ? `${isEmail ? "email" : "phone"}-error`
                  : undefined
              }
            />
            {isTouched && currentErrors.length === 0 && (
              <span
                style={{
                  position: "absolute",
                  right: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontSize: "16px",
                  color: "#22c55e",
                }}
              >
                ✅
              </span>
            )}
            {currentErrors.length > 0 && isTouched && (
              <div
                id={isEmail ? "email-error" : "phone-error"}
                style={{
                  color: "#ef4444",
                  fontSize: "12px",
                  marginTop: "4px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <span style={{ fontWeight: "bold" }}>⚠️</span>
                {currentErrors[0]}
              </div>
            )}
          </div>
        );
      }

      return questionWrapper(
        <ShortAnswerQuestion
          question={question}
          answers={answers}
          onAnswerChange={handleShortAnswerChange}
          questionIndex={globalIndex}
          placeholder=""
        />
      );
    }

    // ESSAY
    if (questionType === "ESSAY") {
      return questionWrapper(
        <div>
          <div
            style={{
              fontSize: "16px",
              lineHeight: "1.6",
              color: "#333",
              fontWeight: "500",
              marginBottom: "16px",
              paddingRight: "80px",
            }}
          >
            {question?.text}
          </div>
          <textarea
            value={answers[question.id] || ""}
            onChange={(e) => handleAnswerSelect(question.id, e.target.value)}
            placeholder="Type your answer here..."
            style={{
              width: "100%",
              minHeight: "120px",
              padding: "12px",
              border: "2px solid #e0e0e0",
              borderRadius: "8px",
              fontSize: "15px",
              fontFamily: "inherit",
              resize: "vertical",
              outline: "none",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#667eea")}
            onBlur={(e) => (e.target.style.borderColor = "#e0e0e0")}
          />
        </div>
      );
    }

    // Fallback
    return questionWrapper(
      <div>
        <div
          style={{
            fontSize: "16px",
            color: "#333",
            marginBottom: "16px",
            paddingRight: "80px",
          }}
        >
          {question?.text}
        </div>
        {question?.options?.length > 0 && (
          <div style={{ display: "grid", gap: "10px" }}>
            {question.options.map((option, idx) => (
              <label
                key={idx}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "12px 16px",
                  background:
                    answers[question.id] === idx ? "#e8f0fe" : "#f8f9fa",
                  border: `2px solid ${
                    answers[question.id] === idx ? "#667eea" : "#e0e0e0"
                  }`,
                  borderRadius: "8px",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
              >
                <input
                  type="radio"
                  checked={answers[question.id] === idx}
                  onChange={() => handleAnswerSelect(question.id, idx)}
                  style={{
                    width: "18px",
                    height: "18px",
                    accentColor: "#667eea",
                  }}
                />
                <span style={{ fontSize: "15px" }}>{option.text}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ============================================
  // MAIN RENDER
  // ============================================
  return (
    <>
      <div
        className="internship-shell"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "#f5f5f5",
          backgroundImage: 'url("/background1.jpg")',
          backgroundSize: "cover",
          backgroundPosition: "center",
          zIndex: 9999,
          display: "flex",
          flexDirection: "column",
          minHeight: "100dvh",
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
        }}
      >
        {/* Mock Exam Banner */}
        {isMockExam && (
          <div
            style={{
              width: "100%",
              background: "#dc2626",
              color: "#fff",
              textAlign: "center",
              fontSize: "12px",
              padding: "8px 12px",
              fontWeight: "500",
            }}
          >
            Warning: This is a Mock Test for practice purposes only — not an
            official exam.
          </div>
        )}

        {/* Header */}
        <div
          className="internship-header"
          style={{
            background: "rgba(255,255,255,0.95)",
            padding: "10px 20px", // reduced padding
            borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
            display: "flex",
            flexDirection: "column",
            gap: "8px", // smaller vertical gaps
            boxShadow: "0 1px 5px rgba(0,0,0,0.08)",
            maxWidth: "1000px",
            width: "100%",
            margin: "0 auto",
          }}
        >
          <div
            className="header-content"
            style={{
              padding: "10px 20px",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            {/* Header row: Title + Logo */}
            <div
              className="internship-headerRow"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <div>
                  <h1
                    style={{
                      fontSize: "24px",
                      fontWeight: "700",
                      margin: 0,
                      color: "#333",
                    }}
                  >
                    {exam?.title || "Exam Assessment"}
                  </h1>
                  <p style={{ fontSize: "11px", margin: 0, color: "#666" }}>
                    Online Assessment • {questions.length} Questions
                  </p>
                </div>
              </div>

              <img
                className="internship-logo"
                src="/nav-logo.png"
                alt="Logo"
                style={{
                  height: "100px",
                  width: "100px",
                  objectFit: "contain",
                }}
              />
            </div>

            {/* Description */}
            <div
              style={{
                background: "rgba(26, 115, 232, 0.05)",
                border: "1px solid rgba(26, 115, 232, 0.2)",
                borderRadius: "6px",
                padding: "10px", // reduced padding
              }}
            >
              <p
                style={{
                  fontSize: "13px",
                  color: "#1a73e8",
                  margin: 0,
                  lineHeight: "1.4",
                }}
              >
                This pre-assessment evaluates Excel proficiency levels among
                AKAH staff for optimal training placement. The test has three
                levels contextualized to AKAH's habitat, disaster risk
                management, and community development work.
              </p>
            </div>

            {/* Contact Info Status */}
            {(nameTouched || emailTouched || phoneTouched) && (
              <div
                style={{
                  background: contactInfoValid.allValid ? "#f0fdf4" : "#fef2f2",
                  border: `1px solid ${
                    contactInfoValid.allValid ? "#bbf7d0" : "#fecaca"
                  }`,
                  borderRadius: "6px",
                  padding: "8px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    marginBottom: "4px",
                  }}
                >
                  <span style={{ fontSize: "14px" }}>
                    {contactInfoValid.allValid ? "✅" : "⚠️"}
                  </span>
                  <span
                    style={{
                      fontWeight: "600",
                      color: contactInfoValid.allValid ? "#166534" : "#991b1b",
                    }}
                  >
                    Contact Information Status
                  </span>
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    color: contactInfoValid.allValid ? "#166534" : "#991b1b",
                  }}
                >
                  {contactInfoValid.allValid
                    ? "All contact information is valid."
                    : contactInfoValid.allFilled
                    ? "Please fix the validation errors."
                    : "Please fill in all required fields."}
                </div>
              </div>
            )}
          </div>

          <div
            className="stats-row"
            style={{
              padding: "8px 20px",
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            {/* Stats row: Questions, Timer, Progress */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                flexWrap: "wrap",
              }}
            >
              {/* Question Counter */}
              <div
                style={{
                  background: "#f0f4ff",
                  padding: "6px 12px",
                  borderRadius: "16px",
                  border: "1px solid #667eea",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    fontSize: "10px",
                    color: "#667eea",
                    marginBottom: "1px",
                  }}
                >
                  Questions
                </div>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "700",
                    color: "#667eea",
                  }}
                >
                  {startIndex + 1}-{endIndex} / {questions.length}
                </div>
              </div>

              {/* Timer */}
              <div
                style={{
                  background: timeLeft < 300 ? "#fef2f2" : "#f0fdf4",
                  padding: "6px 12px",
                  borderRadius: "16px",
                  border: `1px solid ${timeLeft < 300 ? "#ef4444" : "#22c55e"}`,
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    fontSize: "10px",
                    color: timeLeft < 300 ? "#ef4444" : "#22c55e",
                    marginBottom: "1px",
                  }}
                >
                  Time Left
                </div>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "700",
                    color: timeLeft < 300 ? "#ef4444" : "#22c55e",
                  }}
                >
                  {formatTime(timeLeft)}
                </div>
              </div>

              {/* Progress */}
              <div style={{ width: "80px" }}>
                <div
                  style={{
                    fontSize: "10px",
                    color: "#666",
                    marginBottom: "2px",
                    textAlign: "center",
                  }}
                >
                  {Math.round(examProgress)}% Complete
                </div>
                <div
                  style={{
                    height: "6px",
                    background: "#e0e0e0",
                    borderRadius: "3px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${examProgress}%`,
                      height: "100%",
                      background: "linear-gradient(90deg, #667eea, #764ba2)",
                      transition: "width 0.3s ease",
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Question Area */}
        <div
          className="question-area internship-questionArea"
          style={{
            flex: 1,
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "20px",
          }}
        >
          <div
            className="internship-card"
            style={{
              maxWidth: "900px",
              width: "100%",
              background: "rgba(255,255,255,0.9)",
              borderRadius: "16px",
              padding: "30px",
              boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
              backdropFilter: "blur(10px)",
            }}
          >
            {/* Render Questions with Section Headers */}
            {currentQuestions.map((question, index) => {
              const globalIndex = startIndex + index;
              const isFirstInPage = index === 0;
              const isFirstTechnical = globalIndex === firstTechnicalIndex;

              // Check if we need section header
              const sectionInfo = getSectionHeader(question, isFirstTechnical);
              const prevQuestion =
                globalIndex > 0 ? questions[globalIndex - 1] : null;
              const prevSectionInfo = prevQuestion
                ? getSectionHeader(prevQuestion)
                : null;

              // Only show header if it's different from previous
              const showHeader =
                sectionInfo &&
                (isFirstInPage ||
                  sectionInfo.section !== prevSectionInfo?.section);

              // Check if this is the contact info section
              const isContactInfoSection =
                question.id === "cmjdzca9i00yyczak45q0j25t";

              return (
                <React.Fragment key={question.id}>
                  {showHeader && (
                    <SectionHeader
                      title={sectionInfo.title}
                      subtitle={sectionInfo.subtitle}
                      isContactInfo={isContactInfoSection}
                    />
                  )}
                  {question.id === "participant_name" ? (
                    <div style={{ marginBottom: 20, position: "relative" }}>
                      <label
                        style={{
                          fontWeight: 700,
                          display: "block",
                          marginBottom: 8,
                        }}
                      >
                        Please enter your name
                      </label>
                      <input
                        type="text"
                        value={participantName}
                        onChange={handleNameChange}
                        onBlur={() => setNameTouched(true)}
                        placeholder="Your full name"
                        style={{
                          width: "100%",
                          padding: "12px 14px",
                          border:
                            nameErrors.length > 0 && nameTouched
                              ? "2px solid #ef4444"
                              : nameTouched && nameErrors.length === 0
                              ? "2px solid #22c55e"
                              : "2px solid #e5e7eb",
                          borderRadius: "8px",
                          fontSize: "14px",
                          fontFamily: "inherit",
                          outline: "none",
                          transition: "border-color 0.2s ease",
                        }}
                        onFocus={(e) => {
                          if (!(nameErrors.length > 0 && nameTouched)) {
                            e.target.style.borderColor = "#667eea";
                          }
                        }}
                        maxLength={100}
                        autoComplete="name"
                        aria-describedby={
                          nameErrors.length > 0 && nameTouched
                            ? "name-error"
                            : undefined
                        }
                      />
                      {nameTouched && nameErrors.length === 0 && (
                        <span
                          style={{
                            position: "absolute",
                            right: "12px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            fontSize: "16px",
                            color: "#22c55e",
                          }}
                        >
                          ✅
                        </span>
                      )}
                      {nameErrors.length > 0 && nameTouched && (
                        <div
                          id="name-error"
                          style={{
                            color: "#ef4444",
                            fontSize: "12px",
                            marginTop: "4px",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <span style={{ fontWeight: "bold" }}>⚠️</span>
                          {nameErrors[0]}
                        </div>
                      )}
                    </div>
                  ) : (
                    renderQuestion(question, globalIndex)
                  )}
                </React.Fragment>
              );
            })}

            {/* Navigation */}
            <div
              className="internship-nav"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                paddingTop: "24px",
                borderTop: "2px solid #f0f0f0",
                marginTop: "20px",
                flexWrap: "wrap",
                gap: "12px",
              }}
            >
              <button
                className="internship-navBtn"
                onClick={() => {
                  setCurrentPage(Math.max(0, currentPage - 1));
                  // Scroll to top of question area
                  setTimeout(() => {
                    const questionArea =
                      document.querySelector(".question-area");
                    if (questionArea) {
                      questionArea.scrollTop = 0;
                    }
                  }, 0);
                }}
                disabled={currentPage === 0}
                style={{
                  background: currentPage === 0 ? "#f5f5f5" : "#ffffff",
                  color: currentPage === 0 ? "#999" : "#333",
                  border: "2px solid #e0e0e0",
                  padding: "12px 20px",
                  borderRadius: "8px",
                  fontWeight: "600",
                  cursor: currentPage === 0 ? "not-allowed" : "pointer",
                  fontSize: "14px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  transition: "all 0.2s ease",
                }}
              >
                <FiChevronLeft size={18} />
                Previous
              </button>

              <div
                className="internship-navActions"
                style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}
              >
                <button
                  className="internship-actionBtn"
                  onClick={() => effectiveToggleFlag(startIndex)}
                  style={{
                    background: effectiveFlaggedQuestions.has(startIndex)
                      ? "#fef3c7"
                      : "#f5f5f5",
                    color: effectiveFlaggedQuestions.has(startIndex)
                      ? "#d97706"
                      : "#666",
                    border: `2px solid ${
                      effectiveFlaggedQuestions.has(startIndex)
                        ? "#fbbf24"
                        : "#e0e0e0"
                    }`,
                    padding: "10px 16px",
                    borderRadius: "8px",
                    fontWeight: "500",
                    cursor: "pointer",
                    fontSize: "13px",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <FiFlag size={14} />
                  {effectiveFlaggedQuestions.has(startIndex)
                    ? "Flagged"
                    : "Flag"}
                </button>

                <button
                  className="internship-actionBtn"
                  onClick={() => effectiveSetShowQuestionNavigator(true)}
                  style={{
                    background: "#f5f5f5",
                    color: "#666",
                    border: "2px solid #e0e0e0",
                    padding: "10px 16px",
                    borderRadius: "8px",
                    fontWeight: "500",
                    cursor: "pointer",
                    fontSize: "13px",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <FiGrid size={14} />
                  Navigator
                </button>
              </div>

              {endIndex >= questions.length ? (
                <button
                  className="internship-primaryBtn"
                  onClick={() => {
                    console.log(
                      "Finish Exam button clicked - submitting with validation"
                    );
                    console.log("Full answers object:", answers);
                    console.log("Questions count:", questions.length);
                    console.log("Participant name:", participantName);

                    // Exit fullscreen if in fullscreen mode
                    if (document.exitFullscreen) {
                      console.log("Exiting fullscreen");
                      document.exitFullscreen().catch((err) => {
                        console.log("Could not exit fullscreen:", err);
                      });
                    }

                    // Call the enhanced submit function with validation
                    handleSubmitWithValidation(participantName);
                  }}
                  disabled={
                    !contactInfoValid.allValid &&
                    nameTouched &&
                    emailTouched &&
                    phoneTouched
                  }
                  style={{
                    background:
                      !contactInfoValid.allValid &&
                      nameTouched &&
                      emailTouched &&
                      phoneTouched
                        ? "#9ca3af"
                        : "linear-gradient(135deg, #ef4444, #dc2626)",
                    color: "white",
                    border: "none",
                    padding: "12px 24px",
                    borderRadius: "8px",
                    fontWeight: "600",
                    cursor:
                      !contactInfoValid.allValid &&
                      nameTouched &&
                      emailTouched &&
                      phoneTouched
                        ? "not-allowed"
                        : "pointer",
                    fontSize: "14px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    boxShadow:
                      !contactInfoValid.allValid &&
                      nameTouched &&
                      emailTouched &&
                      phoneTouched
                        ? "none"
                        : "0 4px 15px rgba(239, 68, 68, 0.3)",
                    opacity:
                      !contactInfoValid.allValid &&
                      nameTouched &&
                      emailTouched &&
                      phoneTouched
                        ? 0.6
                        : 1,
                  }}
                >
                  Finish Assessment
                  <FiChevronRight size={18} />
                </button>
              ) : (
                <button
                  className="internship-primaryBtn"
                  onClick={() => {
                    setCurrentPage(currentPage + 1);
                    // Scroll to top of question area
                    setTimeout(() => {
                      const questionArea =
                        document.querySelector(".question-area");
                      if (questionArea) {
                        questionArea.scrollTop = 0;
                      }
                    }, 0);
                  }}
                  style={{
                    background: "linear-gradient(135deg, #667eea, #764ba2)",
                    color: "white",
                    border: "none",
                    padding: "12px 24px",
                    borderRadius: "8px",
                    fontWeight: "600",
                    cursor: "pointer",
                    fontSize: "14px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)",
                  }}
                >
                  Next Page
                  <FiChevronRight size={18} />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Styles */}
        <style>{`
          .no-copy {
            -webkit-user-select: none;
            -moz-user-select: none;
            user-select: none;
          }
          .no-copy input, .no-copy textarea {
            -webkit-user-select: text;
            -moz-user-select: text;
            user-select: text;
          }

          /* Responsive layout (InternshipInterface) */
          .internship-shell {
            width: 100%;
          }

          /* Prevent horizontal scrolling on small screens */
          .internship-shell,
          .internship-header,
          .internship-questionArea {
            overflow-x: hidden;
          }

          @media (max-width: 768px) {
            .internship-header {
              padding: 8px 12px !important;
              border-radius: 0;
            }

            /* header-content and stats-row are used for scroll hide/show logic */
            .header-content {
              padding: 8px 12px !important;
              gap: 10px !important;
            }

            .internship-headerRow {
              flex-direction: row;
              align-items: center;
              gap: 10px;
            }

            .internship-headerRow h1 {
              font-size: 18px !important;
              line-height: 1.2;
            }

            .internship-logo {
              width: 52px !important;
              height: 52px !important;
            }

            .stats-row {
              padding: 8px 12px !important;
              gap: 10px !important;
            }

            .internship-questionArea {
              padding: 12px !important;
            }

            .internship-card {
              padding: 14px !important;
              border-radius: 12px !important;
            }

            .internship-nav {
              flex-direction: column;
              align-items: stretch;
              gap: 10px;
            }

            .internship-navBtn,
            .internship-primaryBtn {
              width: 100%;
              justify-content: center;
            }

            .internship-navActions {
              width: 100%;
            }

            .internship-actionBtn {
              flex: 1;
              min-width: 0;
              justify-content: center;
            }

            /* Make the two action buttons sit nicely on one row */
            .internship-navActions {
              display: flex;
              gap: 10px;
            }
          }

          @media (max-width: 380px) {
            .internship-headerRow {
              flex-direction: column;
              align-items: flex-start;
            }

            .internship-logo {
              align-self: flex-end;
            }

            .internship-navActions {
              flex-direction: column;
            }

            .internship-actionBtn {
              width: 100%;
            }
          }
        `}</style>
      </div>

      {/* Question Navigator Modal */}
      {effectiveShowQuestionNavigator && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            zIndex: 10000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
        >
          <div
            style={{
              background: "#ffffff",
              borderRadius: "16px",
              padding: "24px",
              width: "100%",
              maxWidth: "500px",
              maxHeight: "80vh",
              overflow: "auto",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h3 style={{ fontSize: "20px", fontWeight: "700", margin: 0 }}>
                🧭 Question Navigator
              </h3>
              <button
                onClick={() => effectiveSetShowQuestionNavigator(false)}
                style={{
                  background: "#f5f5f5",
                  border: "none",
                  width: "36px",
                  height: "36px",
                  borderRadius: "50%",
                  fontSize: "20px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                ×
              </button>
            </div>

            {/* Legend */}
            <div
              style={{
                display: "flex",
                gap: "16px",
                marginBottom: "20px",
                padding: "12px",
                background: "#f8f9fa",
                borderRadius: "8px",
                flexWrap: "wrap",
                fontSize: "12px",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "6px" }}
              >
                <div
                  style={{
                    width: "14px",
                    height: "14px",
                    background: "#667eea",
                    borderRadius: "3px",
                  }}
                ></div>
                <span>
                  Profile (
                  {
                    questions.filter((q) => PROFILE_QUESTION_IDS.has(q.id))
                      .length
                  }
                  )
                </span>
              </div>
              <div
                style={{ display: "flex", alignItems: "center", gap: "6px" }}
              >
                <div
                  style={{
                    width: "14px",
                    height: "14px",
                    background: "#e3f2fd",
                    border: "1px solid #90caf9",
                    borderRadius: "3px",
                  }}
                ></div>
                <span>Answered ({Object.keys(answers).length})</span>
              </div>
              <div
                style={{ display: "flex", alignItems: "center", gap: "6px" }}
              >
                <FaFlag size={12} color="#f59e0b" />
                <span>Flagged ({effectiveFlaggedQuestions.size})</span>
              </div>
            </div>

            {/* Question Grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(48px, 1fr))",
                gap: "8px",
              }}
            >
              {questions.map((question, index) => {
                const isProfile = PROFILE_QUESTION_IDS.has(question.id);
                const isAnswered = !!answers[question.id];
                const isCurrent =
                  Math.floor(index / questionsPerPage) === currentPage;
                const isFlagged = effectiveFlaggedQuestions.has(index);

                return (
                  <button
                    key={index}
                    onClick={() => navigateToQuestion(index)}
                    style={{
                      padding: "10px",
                      fontSize: "13px",
                      fontWeight: "600",
                      cursor: "pointer",
                      minHeight: "44px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                      borderRadius: "8px",
                      border: isCurrent
                        ? "2px solid #333"
                        : "2px solid transparent",
                      background: isProfile
                        ? isAnswered
                          ? "#c4b5fd"
                          : "#667eea"
                        : isAnswered
                        ? "#bbdefb"
                        : "#f5f5f5",
                      color: isProfile && !isAnswered ? "#fff" : "#333",
                      transition: "all 0.2s ease",
                    }}
                  >
                    {index + 1}
                    {isFlagged && (
                      <FaFlag
                        size={10}
                        style={{
                          position: "absolute",
                          top: "4px",
                          right: "4px",
                          color: "#f59e0b",
                        }}
                      />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Stats */}
            <div
              style={{
                marginTop: "20px",
                padding: "16px",
                background: "linear-gradient(135deg, #f0f4ff, #faf5ff)",
                borderRadius: "12px",
                display: "flex",
                justifyContent: "space-around",
              }}
            >
              <div style={{ textAlign: "center" }}>
                <div
                  style={{
                    fontSize: "28px",
                    fontWeight: "bold",
                    color: "#667eea",
                  }}
                >
                  {Object.keys(answers).length}
                </div>
                <div style={{ fontSize: "12px", color: "#666" }}>Answered</div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div
                  style={{
                    fontSize: "28px",
                    fontWeight: "bold",
                    color: "#f59e0b",
                  }}
                >
                  {effectiveFlaggedQuestions.size}
                </div>
                <div style={{ fontSize: "12px", color: "#666" }}>Flagged</div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div
                  style={{
                    fontSize: "28px",
                    fontWeight: "bold",
                    color: "#9ca3af",
                  }}
                >
                  {questions.length - Object.keys(answers).length}
                </div>
                <div style={{ fontSize: "12px", color: "#666" }}>Remaining</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InternshipInterface;
